import { useEffect } from 'react';
import Alert from '@v2/shared/components/Alert';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import Spinner from '@v2/shared/components/Spinner';
import useToggle from '@v2/shared/lib/useToggle';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import BillingHistory from './BillingHistory';
import Details from './Details';
import DisableAutoRenewalModal from './DisableAutoRenewalModal';
import DelayAutoRenewalModal from './DelayAutoRenewalModal';
import SubscriptionInfo from './SubscriptionInfo';
import useAutoRenewal from './useAutoRenewal';
import { Props } from './types';

/** The contact form's "General Inquiry or Comment" — see ContactForm/subjects.js. */
const FEEDBACK_SUBJECT = 6;

/** The account dashboard, still legacy customer.php — the same entry the menu opens with. */
const ACCOUNT_URL = '/customer?xCmd=account';

const PAYMENT_UPDATED_PARAMETER = 'payment-updated';

/**
 * /my-account/discountlock/{id} — one auto-renewing subscription, and everything a
 * customer can do to it: change the card, delay the renewal, take a retention offer,
 * switch title, or stop it.
 *
 * Off headless_jshop, and the sibling of the list at /my-account/discountlock. The same
 * bargain as the list — the server renders the frame, the browser fetches the
 * subscription under a spinner — and for the same reasons: behind the login wall there is
 * no crawler to render for, and props that are the same for every visitor are props that
 * can be cached.
 *
 * Nothing here names the service. Every brand calls it something else, and the name
 * arrives as a prop off sites config, along with the urls the page links out through —
 * every one of them relative to the site root, so they go through `buildUrl`. See
 * ManageAutoRenewalPagePropsBuilder.
 *
 * The page owns the two flows rather than the box whose buttons open them. Disabling a
 * subscription turns it `canceled`, and Details draws no controls at all for that status
 * — so a modal mounted under there would unmount itself the instant it succeeded, and
 * "you will no longer be charged" would never reach the customer who just asked for it.
 * Up here the dialog outlives the state it changes.
 */
export default function ManageAutoRenewal({
  id,
  autoRenewServiceName,
  listUrl,
  loginUrl,
  confirmUrlTemplate,
  updatePaymentUrlTemplate,
  receiptUrlTemplate,
  paymentUpdated,
}: Props) {
  const { buildUrl } = useUrlGenerator();
  const { autoRenewal, isLoading, notFound, hasFailed, onUpdated } = useAutoRenewal(id, loginUrl);
  const disableModal = useToggle();
  const delayModal = useToggle();

  useEffect(() => {
    if (!paymentUpdated) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete(PAYMENT_UPDATED_PARAMETER);
    window.history.replaceState(window.history.state, '', url.toString());
  }, [paymentUpdated]);

  const content = () => {
    if (isLoading) {
      return (
        <div className="w-full flex items-center justify-center py-10">
          {/* Themed, like the list's — see AutoRenewals. */}
          <Spinner className="text-primary text-3xl" />
        </div>
      );
    }

    if (hasFailed) {
      return <Alert color="danger">Oops, something went wrong. Try again later.</Alert>;
    }

    if (notFound || !autoRenewal) {
      return <div className="font-medium text-slate-600">No subscription found</div>;
    }

    return (
      <>
        {paymentUpdated && <Alert color="success">Your payment method has been updated.</Alert>}

        <Alert color="info">
          <div className="font-semibold mb-1">New! Improved {autoRenewServiceName} Management</div>
          We&apos;ve made some improvements to this page in order to make it easier for you to manage your{' '}
          {autoRenewServiceName} subscriptions.
          <br />
          If you have any questions or feedback on this new experience,{' '}
          <a
            href={buildUrl(`/contact?xSubject=${FEEDBACK_SUBJECT}`)}
            target="_blank"
            rel="noreferrer"
            className="underline font-medium"
          >
            let us know here
          </a>
          .
        </Alert>

        <div className="border-[1.5px] border-solid p-0 md:p-5 rounded-md border-transparent md:border-slate-200 mb-8">
          <div className="grid grid-cols-12 gap-x-0 md:gap-x-8 mb-5 md:mb-0">
            <div className="col-span-12 lg:col-span-6">
              <SubscriptionInfo autoRenewal={autoRenewal} />
            </div>

            <div className="col-span-12 lg:col-span-6">
              <Details
                autoRenewal={autoRenewal}
                autoRenewServiceName={autoRenewServiceName}
                confirmUrlTemplate={confirmUrlTemplate}
                updatePaymentUrlTemplate={updatePaymentUrlTemplate}
                onDisable={disableModal.open}
                onDelay={delayModal.open}
              />

              <BillingHistory autoRenewal={autoRenewal} receiptUrlTemplate={receiptUrlTemplate} />
            </div>
          </div>
        </div>

        <DisableAutoRenewalModal
          isOpen={disableModal.isOpen}
          close={disableModal.close}
          id={id}
          loginUrl={loginUrl}
          autoRenewal={autoRenewal}
          autoRenewServiceName={autoRenewServiceName}
          onUpdated={onUpdated}
        />

        <DelayAutoRenewalModal
          isOpen={delayModal.isOpen}
          close={delayModal.close}
          id={id}
          loginUrl={loginUrl}
          autoRenewal={autoRenewal}
          autoRenewServiceName={autoRenewServiceName}
          onUpdated={onUpdated}
        />
      </>
    );
  };

  return (
    <>
      {/* This page's trail, not the layout's — see SideMenu. Outside everything the api
          decides, so that a subscription still loading, missing or erroring is still a
          page a customer can find their way out of. */}
      <Breadcrumbs
        items={[
          { label: 'Account', href: ACCOUNT_URL },
          { label: autoRenewServiceName, href: listUrl },
          { label: `${autoRenewServiceName} Subscription` },
        ]}
      />

      {content()}
    </>
  );
}

ManageAutoRenewal.layoutName = 'Layouts.Account';
